'use client';

import { defaultLocation, defaultNavigation, SDSContext } from '@repo/sds/providers/SDSProvider/context';
import { DefaultImage } from '@repo/sds/providers/SDSProvider/defaults/DefaultImage';
import type { SDSProviderProps } from '@repo/sds/providers/SDSProvider/types';

/**
 * Provider component for SDS configuration
 *
 * @example
 * ```tsx
 * // Next.js app
 * import Link from 'next/link';
 * import Image from 'next/image';
 * import { useRouter } from 'next/navigation';
 *
 * const router = useRouter();
 * <SDSProvider
 *   linkComponent={Link}
 *   imageComponent={Image}
 *   navigation={{ navigate: router.push, replace: router.replace, back: router.back }}
 * >
 *   <App />
 * </SDSProvider>
 * ```
 *
 * @example
 * ```tsx
 * // React Router app
 * import { Link, useNavigate } from 'react-router-dom';
 *
 * const navigate = useNavigate();
 * <SDSProvider
 *   linkComponent={Link}
 *   navigation={{ navigate, replace: (p) => navigate(p, { replace: true }), back: () => navigate(-1) }}
 * >
 *   <App />
 * </SDSProvider>
 * ```
 */
export function SDSProvider({
  children,
  linkComponent,
  imageComponent = DefaultImage,
  navigation = defaultNavigation,
  location = defaultLocation,
}: SDSProviderProps) {
  return (
    <SDSContext.Provider value={{ linkComponent, imageComponent, navigation, location }}>
      {children}
    </SDSContext.Provider>
  );
}
